/**
 * RequestLiveNowStrip
 * ----------------------------------------------------------------------------
 * Conditional hero strip that only renders when a DJ is currently ON AIR.
 * Reads /api/schedule, matches the current time slot, and lets listeners
 * fire a song request straight to the station via /api/song-request without
 * leaving the hero. Auto-hides between shows so the hero stays clean.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAY_KEYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function parseHourRange(timeStr) {
  if (!timeStr) return null;
  const m = String(timeStr).match(
    /(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?\s*[–-]\s*(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?/i,
  );
  if (!m) return null;
  const to24 = (h, mm, ap) => {
    let n = parseInt(h, 10) + (mm ? parseInt(mm, 10) / 60 : 0);
    if (ap) {
      ap = ap.toUpperCase();
      if (ap === "PM" && parseInt(h, 10) < 12) n += 12;
      if (ap === "AM" && parseInt(h, 10) === 12) n -= 12;
    }
    return n;
  };
  let s = to24(m[1], m[2], m[3] || m[6]);
  let e = to24(m[4], m[5], m[6] || m[3]);
  if (e <= s) e += 24;
  return [s, e];
}

function findLiveShow(schedule) {
  const now = new Date();
  const todayKey = DAY_KEYS[now.getDay()];
  const nowHour = now.getHours() + now.getMinutes() / 60;
  const todayShows = schedule[todayKey] || [];
  for (const s of todayShows) {
    if (!s.live) continue;
    const r = parseHourRange(s.time);
    if (!r) continue;
    if (nowHour >= r[0] && nowHour < r[1]) return s;
  }
  return null;
}

export default function RequestLiveNowStrip() {
  const [show, setShow] = useState(null);
  const [song, setSong] = useState("");
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/schedule`);
        if (cancelled) return;
        const live = findLiveShow(res.data?.schedule || {});
        setShow(live);
      } catch (e) {
        /* silent — no strip is fine */
      }
    };
    load();
    // Poll every 3 min so the strip appears/disappears as shows change.
    const t = setInterval(load, 3 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    const s = song.trim();
    if (!s) {
      toast.error("Type a song and artist first.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/song-request`, {
        name: name.trim() || "",
        song: s.slice(0, 200),
        show: show?.name || "",
        note: "Requested live from the homepage.",
      });
      setSent(true);
      setSong("");
      toast.success(`Request fired to ${show?.name || "the studio"}. Turn it up.`);
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't send request — try again.",
      );
    } finally {
      setSending(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="relative border-y-2 border-red-600 bg-gradient-to-r from-red-600/20 via-black to-black overflow-hidden"
      data-testid="request-live-strip"
    >
      {/* Diagonal red slash accent */}
      <div
        aria-hidden
        className="absolute left-0 top-0 bottom-0 w-2 bg-red-600"
      />

      <div className="relative flex flex-col md:flex-row items-stretch md:items-center gap-3 md:gap-6 px-4 md:px-10 py-3 md:py-4">
        {/* LIVE badge + show name */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-600 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600" />
          </span>
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.35em] uppercase text-red-500 font-display">
              //_on_air_now
            </div>
            <div
              className="font-display text-base md:text-lg tracking-tight text-white uppercase truncate max-w-[260px] md:max-w-none"
              data-testid="request-live-show-name"
            >
              {show.name}
            </div>
          </div>
        </div>

        {/* Request form */}
        <form
          onSubmit={submit}
          className="flex-1 flex flex-col md:flex-row items-stretch gap-2 md:gap-3 min-w-0"
        >
          <input
            type="text"
            value={song}
            onChange={(e) => setSong(e.target.value)}
            placeholder="Request a song from this DJ..."
            className="flex-1 min-w-0 bg-black border border-zinc-700 focus:border-red-500 focus:outline-none px-3 py-2 text-sm text-white placeholder:text-zinc-500 font-mono"
            data-testid="request-live-song-input"
            maxLength={200}
            disabled={sending}
          />
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name (optional)"
            className="w-full md:w-40 bg-black border border-zinc-700 focus:border-red-500 focus:outline-none px-3 py-2 text-sm text-white placeholder:text-zinc-500 font-mono"
            data-testid="request-live-name-input"
            maxLength={80}
            disabled={sending}
          />
          <button
            type="submit"
            disabled={sending || !song.trim()}
            className="font-display tracking-widest uppercase text-sm px-6 py-2 bg-red-600 text-white hover:bg-white hover:text-black transition-colors disabled:opacity-50 disabled:hover:bg-red-600 disabled:hover:text-white whitespace-nowrap"
            data-testid="request-live-submit-btn"
          >
            {sending ? "Firing..." : sent ? "✓ Sent" : "Fire ▶"}
          </button>
        </form>
      </div>
    </div>
  );
}
